import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "../brand/Brand";
import { scrollToId } from "../../hooks/useLenis";

const LINKS = [
  { id: "accueil", label: "Accueil" },
  { id: "le-concept", label: "Le concept" },
  { id: "nos-services", label: "Nos services" },
  { id: "tracabilite", label: "Traçabilité" },
  { id: "le-reseau", label: "Le réseau" },
  { id: "devenir-diallo", label: "Devenir Diallo" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
            : "bg-transparent"
        }`}
      >
        <nav className="mx-auto max-w-7xl px-5 md:px-8 h-[68px] flex items-center justify-between">
          <button
            onClick={() => go("accueil")}
            data-testid="nav-logo"
            aria-label="Diallo - accueil"
            className="shrink-0"
          >
            <Wordmark variant={scrolled ? "red" : "white"} className="h-7 md:h-8" />
          </button>

          <div className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className={`text-sm font-medium transition-colors duration-200 hover:opacity-100 ${
                  scrolled ? "text-diallo-charcoal/80 hover:text-diallo-red" : "text-white/85 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <AnimatePresence>
              {scrolled && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => go("devenir-diallo")}
                  data-testid="nav-cta-btn"
                  className="hidden sm:inline-flex items-center rounded-xl bg-diallo-red px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-diallo-reddeep"
                >
                  Devenir partenaire
                </motion.button>
              )}
            </AnimatePresence>

            <button
              onClick={() => setOpen(true)}
              data-testid="nav-hamburger"
              aria-label="Ouvrir le menu"
              className={`lg:hidden p-2 rounded-lg ${scrolled ? "text-diallo-charcoal" : "text-white"}`}
            >
              <Menu size={26} />
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-diallo-red text-white flex flex-col"
          >
            <div className="flex items-center justify-between h-[68px] px-5">
              <Wordmark variant="white" className="h-7" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Fermer le menu" className="p-2">
                <X size={28} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-link-${l.id}`}
                  className="text-left font-display font-bold text-4xl py-2"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => go("devenir-diallo")}
                data-testid="mobile-cta-btn"
                className="mt-8 rounded-xl bg-white text-diallo-red px-6 py-4 font-semibold text-lg w-full"
              >
                Devenir partenaire
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
